import { useForm, usePage, router } from "@inertiajs/react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import {
  UserPlus,
  Pencil,
  Trash2,
  Mail,
  Shield,
  User as UserIcon,
  UserX,
} from "lucide-react"
import { useSortableData } from "@/hooks/useSortableData"
import { FormError } from "@/components/FormError"

interface Stage {
  id: string
  name: string
  color: string
}

interface User {
  id: number
  user_id: number
  email: string
  name: string | null
  role: "regular" | "admin"
  stage_ids: string[]
  created_at: string
}

interface Invitation {
  id: number
  name: string
  email: string
  role: "regular" | "admin"
  stage_ids: string[]
  expires_at: string
  created_at: string
}

interface PageProps {
  auth: {
    user: { id: number; email: string; name: string | null }
    accounts: Array<{ id: number; name: string; slug: string }>
    currentAccountId: number | null
    role: string | null
  }
  flash: {
    notice: string | null
    alert: string | null
  }
  users: User[]
  invitations: Invitation[]
  stages: Stage[]
  disabled_count: number
  [key: string]: unknown
}

type UserSortKey = "name" | "email" | "role" | "created_at"
type InvitationSortKey = "name" | "email" | "role" | "expires_at"

const userComparators: Record<UserSortKey, (a: User, b: User) => number> = {
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
  email: (a, b) => a.email.localeCompare(b.email),
  role: (a, b) => a.role.localeCompare(b.role),
  created_at: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
}

const invitationComparators: Record<InvitationSortKey, (a: Invitation, b: Invitation) => number> = {
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
  email: (a, b) => (a.email || "").localeCompare(b.email || ""),
  role: (a, b) => (a.role || "").localeCompare(b.role || ""),
  expires_at: (a, b) => new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime(),
}

export default function Users() {
  const { t } = useTranslation()
  const { auth, flash, users, invitations, stages, disabled_count } = usePage<PageProps>().props
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const isAdmin = auth?.role === "admin"

  const {
    sortedData: sortedUsers,
    sortKey: userSortKey,
    sortDirection: userSortDirection,
    handleSort: handleUserSort,
  } = useSortableData(users, "name", userComparators)

  const {
    sortedData: sortedInvitations,
    sortKey: invitationSortKey,
    sortDirection: invitationSortDirection,
    handleSort: handleInvitationSort,
  } = useSortableData(invitations, "name", invitationComparators)

  const inviteForm = useForm({
    name: "",
    email: "",
    role: "regular",
    stage_ids: [] as string[],
  })

  const editForm = useForm({
    name: "",
    email: "",
    role: "",
    stage_ids: [] as string[],
  })

  function handleInviteSubmit(e: React.FormEvent) {
    e.preventDefault()
    inviteForm.post("/users/invite", {
      preserveState: true,
      onSuccess: () => {
        inviteForm.reset()
        setIsInviteDialogOpen(false)
      },
    })
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingUser) return

    editForm.patch(`/users/${editingUser.id}`, {
      preserveState: true,
      onSuccess: () => {
        setEditingUser(null)
        editForm.reset()
      },
    })
  }

  function openEditDialog(user: User) {
    setEditingUser(user)
    editForm.setData({
      name: user.name || "",
      email: user.email,
      role: user.role,
      stage_ids: user.stage_ids || [],
    })
  }

  function toggleInviteStage(stageId: string) {
    const currentIds = inviteForm.data.stage_ids
    if (currentIds.includes(stageId)) {
      inviteForm.setData("stage_ids", currentIds.filter(id => id !== stageId))
    } else {
      inviteForm.setData("stage_ids", [...currentIds, stageId])
    }
  }

  function toggleEditStage(stageId: string) {
    const currentIds = editForm.data.stage_ids
    if (currentIds.includes(stageId)) {
      editForm.setData("stage_ids", currentIds.filter(id => id !== stageId))
    } else {
      editForm.setData("stage_ids", [...currentIds, stageId])
    }
  }

  function handleDisableUser(user: User) {
    if (confirm(t("users.confirmDisable"))) {
      router.post(`/users/${user.id}/deactivation`)
    }
  }

  function handleCancelInvitation(id: number) {
    if (confirm(t("users.confirmCancelInvite"))) {
      router.delete(`/users/invitations/${id}`)
    }
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const isCurrentUser = (user: User) => user.user_id === auth.user.id

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      {flash.notice && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-md">
          {flash.notice}
        </div>
      )}
      {flash.alert && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-md">
          {flash.alert}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("users.title")}</h2>
            <p className="text-muted-foreground">{t("users.subtitle")}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => router.get("/users/disabled")}>
              <UserX className="w-4 h-4 mr-2" />
              {t("users.disabledUsersButton")}
              {disabled_count > 0 && ` (${disabled_count})`}
            </Button>

            {/* Invite Dialog */}
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t("users.inviteButton")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleInviteSubmit}>
                  <DialogHeader>
                    <DialogTitle>{t("users.inviteButton")}</DialogTitle>
                    <DialogDescription>
                      {t("users.inviteDescription")}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-name">{t("users.nameLabel")}</Label>
                      <Input
                        id="invite-name"
                        value={inviteForm.data.name}
                        onChange={(e) => inviteForm.setData("name", e.target.value)}
                        placeholder={t("users.namePlaceholder")}
                        required
                      />
                      <FormError error={inviteForm.errors.name} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-email">{t("users.emailLabel")}</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteForm.data.email}
                        onChange={(e) => inviteForm.setData("email", e.target.value)}
                        placeholder={t("users.emailPlaceholder")}
                        required
                      />
                      <FormError error={inviteForm.errors.email} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-role">{t("users.roleLabel")}</Label>
                      <Select
                        value={inviteForm.data.role}
                        onValueChange={(value) => inviteForm.setData("role", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="regular">{t("users.roleRegular")}</SelectItem>
                          <SelectItem value="admin">{t("users.roleAdmin")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {inviteForm.data.role === "regular" && stages.length > 0 && (
                      <div className="space-y-3">
                        <Label>{t("users.stagePermissions")}</Label>
                        <p className="text-sm text-muted-foreground">{t("users.stagePermissionsHint")}</p>
                        <div className="space-y-2">
                          {stages.map((stage) => (
                            <div key={stage.id} className="flex items-center justify-between">
                              <Label htmlFor={`invite-stage-${stage.id}`} className="font-normal cursor-pointer flex items-center gap-2">
                                <span
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: stage.color }}
                                />
                                {stage.name}
                              </Label>
                              <Switch
                                id={`invite-stage-${stage.id}`}
                                checked={inviteForm.data.stage_ids.includes(stage.id)}
                                onCheckedChange={() => toggleInviteStage(stage.id)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsInviteDialogOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button type="submit" disabled={inviteForm.processing}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t("users.sendInvite")}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Edit Dialog */}
          <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
            <DialogContent>
              <form onSubmit={handleEditSubmit}>
                <DialogHeader>
                  <DialogTitle>{t("users.editUser")}</DialogTitle>
                  <DialogDescription>{t("users.editUserDescription")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t("users.nameLabel")}</Label>
                    <Input
                      id="edit-name"
                      value={editForm.data.name}
                      onChange={(e) => editForm.setData("name", e.target.value)}
                      placeholder={t("users.namePlaceholder")}
                    />
                    <FormError error={editForm.errors.name} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">{t("users.emailLabel")}</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editForm.data.email}
                      onChange={(e) => editForm.setData("email", e.target.value)}
                      required
                    />
                    <FormError error={editForm.errors.email} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-role">{t("users.roleLabel")}</Label>
                    <Select
                      value={editForm.data.role}
                      onValueChange={(value) => editForm.setData("role", value)}
                      disabled={!isAdmin || (editingUser?.user_id === auth?.user?.id)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">{t("users.roleRegular")}</SelectItem>
                        <SelectItem value="admin">{t("users.roleAdmin")}</SelectItem>
                      </SelectContent>
                    </Select>
                    {(!isAdmin || editingUser?.user_id === auth?.user?.id) && (
                      <p className="text-sm text-muted-foreground">
                        {editingUser?.user_id === auth?.user?.id
                          ? t("users.cannotChangeOwnRole")
                          : t("users.roleDisabledHint")}
                      </p>
                    )}
                    <FormError error={editForm.errors.role} />
                  </div>
                  {editForm.data.role === "regular" && stages.length > 0 && (
                    <div className="space-y-3">
                      <Label>{t("users.stagePermissions")}</Label>
                      <p className="text-sm text-muted-foreground">{t("users.stagePermissionsHint")}</p>
                      <div className="space-y-2">
                        {stages.map((stage) => (
                          <div key={stage.id} className="flex items-center justify-between">
                            <Label htmlFor={`edit-stage-${stage.id}`} className="font-normal cursor-pointer flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: stage.color }}
                              />
                              {stage.name}
                            </Label>
                            <Switch
                              id={`edit-stage-${stage.id}`}
                              checked={editForm.data.stage_ids.includes(stage.id)}
                              onCheckedChange={() => toggleEditStage(stage.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingUser(null)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button type="submit" disabled={editForm.processing}>
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Users Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="w-5 h-5" />
              {t("users.usersSection")}
            </CardTitle>
            <CardDescription>{t("users.usersDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    label={t("users.nameLabel")}
                    sortKey="name"
                    currentSortKey={userSortKey}
                    direction={userSortDirection}
                    onSort={handleUserSort}
                  />
                  <SortableTableHead
                    label={t("users.emailLabel")}
                    sortKey="email"
                    currentSortKey={userSortKey}
                    direction={userSortDirection}
                    onSort={handleUserSort}
                  />
                  <SortableTableHead
                    label={t("users.roleLabel")}
                    sortKey="role"
                    currentSortKey={userSortKey}
                    direction={userSortDirection}
                    onSort={handleUserSort}
                  />
                  <SortableTableHead
                    label={t("users.joinedAt")}
                    sortKey="created_at"
                    currentSortKey={userSortKey}
                    direction={userSortDirection}
                    onSort={handleUserSort}
                  />
                  <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((user, index) => (
                  <TableRow key={user.id} className={index % 2 === 1 ? "bg-muted" : ""}>
                    <TableCell className="font-medium">
                      {user.name || t("users.noName")}
                      {isCurrentUser(user) && (
                        <span className="text-muted-foreground text-sm ml-2">{t("common.you")}</span>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        {user.role === "admin" ? t("users.roleAdmin") : t("users.roleRegular")}
                      </span>
                    </TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(user)}
                          title={t("users.editUser")}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!isCurrentUser(user) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDisableUser(user)}
                            className="text-destructive hover:text-destructive"
                            title={t("users.disableUser")}
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Invitations Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {t("users.invitationsSection")}
            </CardTitle>
            <CardDescription>{t("users.invitationsDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            {invitations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t("users.noInvitations")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead
                      label={t("users.nameLabel")}
                      sortKey="name"
                      currentSortKey={invitationSortKey}
                      direction={invitationSortDirection}
                      onSort={handleInvitationSort}
                    />
                    <SortableTableHead
                      label={t("users.emailLabel")}
                      sortKey="email"
                      currentSortKey={invitationSortKey}
                      direction={invitationSortDirection}
                      onSort={handleInvitationSort}
                    />
                    <SortableTableHead
                      label={t("users.roleLabel")}
                      sortKey="role"
                      currentSortKey={invitationSortKey}
                      direction={invitationSortDirection}
                      onSort={handleInvitationSort}
                    />
                    <SortableTableHead
                      label={t("users.expiresAt")}
                      sortKey="expires_at"
                      currentSortKey={invitationSortKey}
                      direction={invitationSortDirection}
                      onSort={handleInvitationSort}
                    />
                    <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedInvitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell className="font-medium">{invitation.name}</TableCell>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {invitation.role === "admin" ? t("users.roleAdmin") : t("users.roleRegular")}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(invitation.expires_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCancelInvitation(invitation.id)}
                          className="text-destructive hover:text-destructive"
                          title={t("common.cancel")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
